import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Shield, Award, Eye, Download, CalendarRange, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { viewDocument, downloadDocument } from '../../services/downloadService';
import { getAuthHeader } from '../../services/tokenService';

const ETATS = {
  en_cours: { label: 'En cours', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  a_renouveler: { label: 'À renouveler', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  a_venir: { label: 'À venir', cls: 'bg-sky-100 text-sky-700 border-sky-200' },
  expire: { label: 'Expiré', cls: 'bg-slate-200 text-slate-600 border-slate-300' },
  resilie: { label: 'Résilié', cls: 'bg-red-100 text-red-700 border-red-200' },
};
const ORDRE = ['en_cours', 'a_renouveler', 'a_venir', 'expire', 'resilie'];
const eur = (v) => `${Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const jour = (d) => (d ? String(d).slice(0, 10).split('-').reverse().join('/') : '—');

const Etat = ({ e, testid }) => <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${ETATS[e]?.cls}`} data-testid={testid}>{ETATS[e]?.label || e}</span>;

const Montants = ({ d, id }) => (
  <div className="grid grid-cols-3 gap-2 text-right text-xs min-w-[260px]">
    <div><p className="text-slate-400">Montant</p><p className="font-semibold text-slate-800" data-testid={`${id}-total`}>{eur(d.montant_total)}</p></div>
    <div><p className="text-slate-400">Acquitté</p><p className="font-semibold text-emerald-700" data-testid={`${id}-acquitte`}>{eur(d.montant_acquitte)}</p></div>
    <div><p className="text-slate-400">Dû</p><p className={`font-semibold ${d.montant_du > 0 ? 'text-red-600' : 'text-slate-500'}`} data-testid={`${id}-du`}>{eur(d.montant_du)}</p></div>
  </div>
);

const Doc = ({ doc }) => (
  <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white pl-2 text-xs">
    <span className="max-w-[220px] truncate">{doc.nom || doc.titre || doc.type}</span>
    <Button variant="ghost" size="sm" onClick={() => viewDocument(doc)} data-testid={`dispo-doc-voir-${doc.id}`}><Eye className="h-3.5 w-3.5" /></Button>
    <Button variant="ghost" size="sm" onClick={() => downloadDocument(doc)} data-testid={`dispo-doc-telecharger-${doc.id}`}><Download className="h-3.5 w-3.5" /></Button>
  </div>
);

const Ligne = ({ d, docs }) => (
  <div className={`rounded-xl border p-3 ${d.etat === 'en_cours' ? 'border-emerald-200 bg-emerald-50/60' : d.etat === 'a_renouveler' ? 'border-amber-200 bg-amber-50/60' : 'border-slate-200 bg-slate-50'}`} data-testid={`dispositif-${d.souscription_id || d.adhesion_id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-900">
          {d.adhesion_id ? <Award className="h-4 w-4 text-indigo-500" /> : <Shield className="h-4 w-4 text-emerald-600" />}
          {d.dispositif || d.plan}{d.est_avenant && <span className="text-[11px] text-violet-700">(avenant)</span>}
          <Etat e={d.etat} testid={`dispositif-etat-${d.souscription_id || d.adhesion_id}`} />
        </p>
        <p className="mt-1 flex items-center gap-1 text-xs text-slate-500"><CalendarRange className="h-3.5 w-3.5" />Du {jour(d.debut)} au {jour(d.fin)}{d.reference && !d.adhesion_id && <span className="ml-2 font-mono">{d.reference}</span>}</p>
      </div>
      <Montants d={d} id={`dispositif-${d.souscription_id || d.adhesion_id}`} />
    </div>
    {docs.length > 0 && <div className="mt-2 flex flex-wrap gap-2">{docs.map((doc) => <Doc key={doc.id} doc={doc} />)}</div>}
  </div>
);

export default function DispositifsMembreSection({ membreId, membreDocuments }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!membreId) return;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/membres/${membreId}/dispositifs`, { headers: getAuthHeader() })
      .then((r) => setData(r.data)).catch(() => setData({ dispositifs: [], adhesions_ftpe: [] }));
  }, [membreId]);
  if (!data) return <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Chargement des dispositifs…</div>;
  const docsDe = (sid) => [...(membreDocuments?.attestations_rcds_signees || []), ...(membreDocuments?.conditions_signees || [])].filter((x) => x.souscription_id === sid);
  const tri = (l) => [...l].sort((a, b) => ORDRE.indexOf(a.etat) - ORDRE.indexOf(b.etat));
  const compte = (e) => [...data.dispositifs, ...data.adhesions_ftpe].filter((d) => d.etat === e).length;
  return (
    <section className="space-y-3" data-testid="dispositifs-membre-section">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-semibold text-slate-900"><Shield className="h-5 w-5 text-emerald-500" />Dispositifs en cours</h3>
        <div className="flex flex-wrap gap-1.5">{ORDRE.filter(compte).map((e) => <span key={e} className={`text-[11px] px-2 py-0.5 rounded-full border ${ETATS[e].cls}`} data-testid={`dispositifs-compteur-${e}`}>{ETATS[e].label} : {compte(e)}</span>)}</div>
      </div>
      {data.dispositifs.length === 0 && <p className="text-sm text-slate-500" data-testid="dispositifs-aucun">Aucun dispositif signé.</p>}
      {tri(data.dispositifs).map((d) => <Ligne key={d.souscription_id} d={d} docs={[...docsDe(d.souscription_id), ...d.recus]} />)}
      <h4 className="pt-2 text-sm font-semibold text-slate-700">Adhésion FTPE</h4>
      {data.adhesions_ftpe.length === 0 && <p className="text-sm text-slate-500" data-testid="adhesion-ftpe-aucune">Aucune adhésion FTPE.</p>}
      {tri(data.adhesions_ftpe).map((a) => <Ligne key={a.adhesion_id} d={a} docs={[]} />)}
      {(() => {
        const connus = new Set(data.dispositifs.map((d) => d.souscription_id));
        const orphelins = (membreDocuments?.attestations_rcds_signees || []).filter((x) => !connus.has(x.souscription_id));
        return orphelins.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="dispositifs-documents-hors-dispositif">
            <span className="w-full text-xs text-slate-500">Autres attestations RCDS</span>
            {orphelins.map((doc) => <Doc key={doc.id} doc={doc} />)}
          </div>
        );
      })()}
      <p className="text-[11px] text-slate-400">« À renouveler » : échéance dans les {data.regle_renouvellement_jours} jours.</p>
    </section>
  );
}
